import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RoutePagesSection from "@/components/RoutePagesSection";
import { Plane, Briefcase, Calendar, Clock, MapPin, Users, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ServicesPage() {
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/cms/site-settings`);
        setSiteSettings(response.data.settings);
      } catch (error) {
        console.error("Failed to fetch settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const contact = siteSettings?.contact || {};

  const services = [
    { icon: Plane, title: "Airport Transfers", description: "Reliable pickups and drop-offs at all major UK airports including Heathrow, Gatwick, Luton, and Stansted." },
    { icon: Briefcase, title: "Corporate Travel", description: "Professional transportation for business executives and corporate events with premium vehicles." },
    { icon: Calendar, title: "Event Transport", description: "Special occasion transport for weddings, parties, and corporate events." },
    { icon: Clock, title: "Hourly Hire", description: "Flexible hourly booking for shopping trips, sightseeing, or business meetings." },
    { icon: MapPin, title: "Long Distance", description: "Comfortable long-distance journeys across the UK with experienced drivers." },
    { icon: Users, title: "Group Travel", description: "Spacious MPVs and executive vehicles for group transportation needs." }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">Comprehensive transportation solutions tailored to your needs</p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <service.icon className="w-12 h-12 text-orange-500 mb-4" />
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <Link to="/book" className="text-orange-500 font-medium hover:text-orange-600">Book Now →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need a Custom Solution?</h2>
          <p className="text-orange-100 mb-8">Contact us for personalized transportation services</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book" className="px-8 py-4 bg-white text-orange-500 rounded-xl font-semibold hover:bg-gray-100">Get Quote</Link>
            {contact.phone && (<a href={`tel:${contact.phone}`} className="px-8 py-4 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 flex items-center gap-2"><Phone size={20} />{contact.phone}</a>)}
          </div>
        </div>
      </section>

      {/* Route Pages */}
      <RoutePagesSection />

      <Footer />
    </div>
  );
}
